import React, { useMemo } from "react";
import { Layout, Tooltip, Drawer } from "antd";
import {
  HomeOutlined,
  SettingOutlined,
  AuditOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  TeamOutlined,
  SafetyOutlined,
  BarChartOutlined,
  HeartOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

export default function Sidebar({
  collapsed,
  onCollapse,
  asDrawer = false,
  drawerOpen = false,
  onDrawerClose = () => {},
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasAnyPermission } = useAuth();

  const navItems = useMemo(() => {
    const sections = [];

    sections.push({
      section: "Overview",
      items: [
        { key: "/home", icon: <HomeOutlined />, label: "Home" },
      ],
    });

    const managementItems = [];
    if (hasAnyPermission("config:read")) {
      managementItems.push({ key: "/configuration/editor", icon: <SettingOutlined />, label: "Configuration" });
    }
    if (hasAnyPermission("audit:view")) {
      managementItems.push({ key: "/audit-logs", icon: <AuditOutlined />, label: "Audit Logs" });
    }
    if (managementItems.length > 0) {
      sections.push({ section: "Management", items: managementItems });
    }

    const adminItems = [];
    if (hasAnyPermission("users:manage")) {
      adminItems.push({ key: "/users", icon: <TeamOutlined />, label: "Users" });
    }
    if (hasAnyPermission("roles:edit")) {
      adminItems.push({ key: "/roles", icon: <SafetyOutlined />, label: "Roles" });
    }
    if (adminItems.length > 0) {
      sections.push({ section: "Administration", items: adminItems });
    }

    if (hasAnyPermission("analytics:view_google_analytics")) {
      sections.push({
        section: "Analytics",
        items: [
          { key: "/analytics/google", icon: <BarChartOutlined />, label: "Google Analytics" },
        ],
      });
    }

    if (hasAnyPermission("monitoring:view_status_page")) {
      sections.push({
        section: "Monitoring",
        items: [
          { key: "/status", icon: <HeartOutlined />, label: "Status Page" },
        ],
      });
    }

    return sections;
  }, [hasAnyPermission]);

  // Inside the Drawer we always render in expanded mode (no point in
  // icon-only when the user has explicitly opened the drawer).
  const expanded = asDrawer ? true : !collapsed;

  const handleNavigate = (key) => {
    navigate(key);
    if (asDrawer) onDrawerClose();
  };

  const renderNavItem = (item) => {
    const isActive = location.pathname === item.key;
    const content = (
      <div
        key={item.key}
        className={`sidebar-nav-item${isActive ? " active" : ""}`}
        onClick={() => handleNavigate(item.key)}
      >
        <span className="nav-icon">{item.icon}</span>
        <span className="sidebar-nav-label">{item.label}</span>
      </div>
    );

    if (!expanded) {
      return (
        <Tooltip key={item.key} title={item.label} placement="right">
          {content}
        </Tooltip>
      );
    }
    return content;
  };

  // Body content shared between Sider and Drawer modes.
  const body = (
    <>
      <div className="sidebar-logo">
        <div className="sidebar-logo-icon">SQ</div>
        <span className="sidebar-logo-text">SafetyQube</span>
      </div>

      <div className="sidebar-nav">
        {navItems.map((section) => (
          <React.Fragment key={section.section}>
            <div className="sidebar-section-label">{section.section}</div>
            {section.items.map(renderNavItem)}
          </React.Fragment>
        ))}
      </div>

      {!asDrawer && (
        <div className="sidebar-toggle">
          <button onClick={() => onCollapse(!collapsed)}>
            {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          </button>
        </div>
      )}
    </>
  );

  if (asDrawer) {
    return (
      <Drawer
        open={drawerOpen}
        onClose={onDrawerClose}
        placement="left"
        width={240}
        closable={false}
        styles={{ body: { padding: 0, background: "var(--color-sidebar-bg)" } }}
        className={`sidebar sidebar-drawer${expanded ? "" : " sidebar-collapsed"}`}
      >
        {body}
      </Drawer>
    );
  }

  return (
    <Layout.Sider
      collapsible
      collapsed={collapsed}
      onCollapse={onCollapse}
      width={240}
      collapsedWidth={68}
      className={`sidebar sidebar-sider${collapsed ? " sidebar-collapsed" : ""}`}
    >
      {body}
    </Layout.Sider>
  );
}
